import type { ReactNode, SelectHTMLAttributes } from 'react'
import cx from 'classnames'
import styles from './Select.module.css'
import { type ControlStatus } from '@/components/inputs/control-status'
import {
  type ControlSize,
  useControlSize,
} from '@/components/inputs/control-size'
import { Icon } from '@/components/Icon'
import { match } from '@/utils/pattern-match-util'

/**
 * A `[value]` or `[value, label]` option.
 */
export type SelectItem<V> = readonly [V] | readonly [V, string]

export interface SelectProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'disabled' | 'size'
> {
  isDisabled?: boolean
  status?: ControlStatus
  value?: string
  items?: readonly SelectItem<unknown>[]
  placeholder?: string | null
  children?: ReactNode
  /**
   * Defaults to the size of the surrounding `ControlSizeContext`.
   */
  size?: ControlSize
}

/**
 * Native select, rendering either `items` (submitting their index) or
 * `children` as its options.
 */
export function Select({
  className,
  value,
  onChange,
  status = 'default',
  items,
  children,
  placeholder,
  size: sizeProp,
  isDisabled,
  ...rest
}: SelectProps) {
  const size = useControlSize(sizeProp)
  const sizeClassName = match(size, {
    normal: undefined,
    compact: styles.compact,
    small: styles.small,
  })
  const icon = (
    <div className={styles.selectArrow}>
      <Icon size={size === 'normal' ? 24 : 16} rotate={90} name="arrow" />
    </div>
  )

  if (!items) {
    return (
      <div className={styles.customSelect}>
        <select
          className={cx(
            styles.select,
            sizeClassName,
            className,
            styles[status],
          )}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          {...rest}
        >
          {children}
        </select>
        {icon}
      </div>
    )
  }

  const selectValue = !value ? -1 : items.findIndex(([key]) => key === value)
  return (
    <div className={styles.customSelect}>
      <select
        className={cx(styles.select, sizeClassName, className, styles[status])}
        onChange={onChange}
        value={selectValue === -1 ? '' : selectValue}
        disabled={isDisabled}
        {...rest}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {items.map((item, index) => (
          <option key={index} value={index}>
            {item[1] ? item[1] : String(item[0])}
          </option>
        ))}
      </select>
      {icon}
    </div>
  )
}
